import { t } from 'react-switch-lang';
import { Card, ListGroup } from 'react-bootstrap';
import { useRouter } from 'next/router';
import { Form } from 'react-final-form';
import { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import Button from '../../Button';
import styles from '../../../styles/ReviewStep.module.scss';
import { events, logAmpEvent } from '../../../utils/Amplitude';
import { DISPATCHERS, setReferralUrl, usePaymentProcessContext } from '../../../context/PaymentProcessContext';
import { RecipientLogo } from '../PaymentInformation';
import ReplaceTextWithElement from '../../ReplaceTextWithElement';
import { TextField } from '../../InputFields';
import { getText, executeReCaptcha, invalidFormAlert, formatAsTestId } from '../../../utils/Helpers';
import { beginPayment, apiErrorHandled } from '../../../utils/ApiClient';
import Alert from '../../Alert';
import PayPal, { payPalRedirectError } from '../PayPal';
import ConfirmationModal from '../../ConfirmationModal';
import GoogleRecaptchaDisclaimer from '../../GoogleRecaptchaDisclaimer';
import { toErrorPage } from '../../ErrorDisplay';
import { isProduction } from '../../../utils/HostingEnv';
import { FormatAmount } from '../../../utils/Validations';
import { calculateFee, PaymentOptionImages } from '../PaymentOptionCard';
import { moveFocusTo } from '../../../utils/Accessibility';

const additionalPaymentOptionInfoStrings = {
  Credit: 'ReviewPage_CreditCard_Instructions',
  InPerson: 'ReviewInstructionsInPerson',
  PayPal: 'ReviewPage_PayPal_Instructions',
  eTransfer: 'ReviewInstructionseTransfer',
  PeoplesRFM: 'ReviewInstructionsPeoplesRFM',
};

function AccountDetailsText({ sortedAccountDetails, partner }) {
  return (
    sortedAccountDetails.map(([key, value]) => {
      /** @type {import('../../../context/PaymentProcessContext').Field} */
      let field;
      switch (key) {
        case 'Email':
          field = {
            Label: {
              En: t('PaymentProcess_AccountDetails_EmailLabel', null, 'en'),
              Fr: t('PaymentProcess_AccountDetails_EmailLabel', null, 'fr'),
            },
          };
          break;
        case 'FullName':
          field = {
            Label: partner.PSWebConfig.AccountNameLabel,
          };
          break;
        default:
          field = partner.PSWebConfig.Fields.find((f) => f.DBField === key);
      }
      if (!field || field.Type === 'Hidden') return null;
      return (
        <p key={key} className={styles.infoHeading}>
          <b>
            {`${getText(field.Label)}: `}
          </b>
          <span data-testid={formatAsTestId(`payment account detail ${key}`)}>{value}</span>
        </p>
      );
    })
  );
}

export default function Review() {
  const {
    dispatchPaymentProcess,
    paymentProcess: {
      paymentOption,
      partner,
      accountDetails,
      isInvoice,
      referral,
      siteMode,
    },
  } = usePaymentProcessContext();
  const router = useRouter();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const additionalPaymentOptionInfo = additionalPaymentOptionInfoStrings[paymentOption?.Name];
  const isPOCreditCard = paymentOption?.Name === 'Credit';
  const isPOPayPal = paymentOption?.Name === 'PayPal';
  const isPOPeoplesRFM = paymentOption?.Name === 'PeoplesRFM';
  const isPOInPerson = paymentOption?.Name === 'InPerson';
  const reCaptchaRef = useRef(null);

  const [reCaptchaAlertMsg, setReCaptchaAlertMsg] = useState('');
  const [isReCaptchaChallenge, setIsReCaptchaChallenge] = useState(false);
  const payPalOnClick = (_, actions) => {
    logAmpEvent(events.USER_CLICKED_CONTINUE_BUTTON, { Step: 'Payment Review' });
    // https://developer.paypal.com/docs/checkout/standard/customize/validate-user-input/#asynchronous-validation-
    // if reCAPTCHA challenge was triggered, return actions.reject() which will
    // close the PayPal popup so that the user can complete the challenge
    return executeReCaptcha(reCaptchaRef.current, 'CHALLENGE')
      .then(actions.resolve)
      .catch(() => {
        setReCaptchaAlertMsg('');
        setIsReCaptchaChallenge(true);
        return actions.reject();
      });
  };

  const onSubmit = async (values) => {
    setLoading(true);
    setError('');

    let Referral = referral;
    if (document.referrer) { // takes precendence over referral from prepopulate
      Referral = setReferralUrl(referral, document.referrer);
    }

    const reCaptchaToken = await executeReCaptcha(reCaptchaRef.current, 'CANCEL', () => setLoading(false)).catch(() => '');
    if (!reCaptchaToken) return undefined;
    const auth = `reCAPTCHA ${reCaptchaToken}`;

    reCaptchaRef.current.reset();
    const resp = await beginPayment(auth, {
      PartnerSeq: partner.PartnerSeq,
      PaymentOption: paymentOption?.Name,
      Fee: paymentOption?.totalFee.toFixed(2),
      Fields: accountDetails,
      Language: router.locale,
      PostalCode: isPOCreditCard ? values.postalCode.trim() : '',
      ReturnUrl: isPOPeoplesRFM ? `${window.location.origin}/${router.locale}/complete-payment` : '',
      Referral,
      SiteMode: siteMode === 'Intuit' ? siteMode : '',
    });
    if (resp.Result === 11 || resp.Result === 12) {
      const errorPage = toErrorPage(resp.Description, isProduction ? '' : `Debug: ${resp.ErrorDebug}`);
      if (isPOPayPal) throw payPalRedirectError(errorPage);
      router.push(errorPage);
      return undefined;
    }
    let errMsgCallback = setError;
    if (isPOPayPal) errMsgCallback = (msg) => { throw payPalRedirectError(toErrorPage(msg)); };
    if (apiErrorHandled(resp, errMsgCallback, router)) return undefined;
    const {
      Result,
      Description,
      PaymentAmount,
      Fee,
      TotalAmount,
      SessionToken,
      ...paymentCode
    } = resp;
    dispatchPaymentProcess(
      DISPATCHERS.BEGIN_PAYMENT(paymentCode, Fee, PaymentAmount, TotalAmount, SessionToken)
    );

    // stay on current page for PayPal, PayPal button opens popup
    if (values === 'PAYPAL') return paymentCode.OrderId;

    if (isPOPeoplesRFM) { // redirect to hosted payment page for PeoplesRFM
      window.location.href = paymentCode.PaymentUrl;
    } else { // otherwise navigate to complete payment page
      router.push('/complete-payment');
    }
    return undefined;
  };

  const validate = (values) => {
    const errors = {};
    if (!values.postalCode || values.postalCode?.trim().length < 1) {
      errors.postalCode = t('Validation_Required');
    }
    if (!values.postalCode || values.postalCode?.trim().length > 20) {
      errors.postalCode = t('Validation_Length_Max').replace('xMax', Number(20));
    }
    return errors;
  };

  const onRecaptchaError = () => {
    logAmpEvent(events.GOOGLE_RECAPTCHA_ERROR);
    setError(t('GoogleRecaptchaFailureMsg'));
    setLoading(false);
  };

  const reCaptchaOnChange = (token) => {
    if (token && isReCaptchaChallenge) {
      setIsReCaptchaChallenge(false);
      setReCaptchaAlertMsg(t('ReviewPage_PayPal_Recaptcha_Alert'));
      moveFocusTo('.payPalButtonContainer');
    }
    if (token === null) {
      // reCAPTCHA expired TODO: change alert from success to error
    }
  };

  const handleCancel = () => {
    dispatchPaymentProcess(DISPATCHERS.START_OVER());
    router.push('/');
  };

  // Desired order of the fields in account details section
  const fieldOrder = [
    'Email',
    'FullName',
    'ClientRef',
    'PARTNERFIELD01',
    'PARTNERFIELD02',
    'PARTNERFIELD03',
    'PARTNERFIELD04',
    'PARTNERFIELD05',
  ];
  const sortedAccountDetails = Object.entries(accountDetails || {})
    .sort(([keyA], [keyB]) => fieldOrder.indexOf(keyA) - fieldOrder.indexOf(keyB));
  const paymentAmountEntry = sortedAccountDetails.find(([key]) => key === 'PaymentAmount');
  const paymentAmount = paymentAmountEntry ? Number(paymentAmountEntry[1]) : 0;
  const calculatedFeeString = calculateFee(paymentOption?.FeeV2, paymentAmount)?.feeString;

  useEffect(() => {
    logAmpEvent(events.USER_VIEWED_REVIEW_PAGE);
  }, []);

  let submit;
  return (
    <>
      <div className={styles.reviewCardsContainer}>
        { /* Error Alert */ }
        <Alert msg={error} />
        { /* Page Title */ }
        <h1 className={styles.reviewCardsContainerTitle}>{t('ReviewPage_Heading_Title')}</h1>
        { /* Payment Recipient */ }
        <Card className={styles.reviewCard}>
          <ListGroup variant="flush" as="ul">
            <ListGroup.Item as="li">
              <h2 className={styles.reviewCardTitle}>{t('PaymentInfo_RecipientTitle')}</h2>
              <RecipientLogo className="mt-3" partner={partner} />
              {partner?.LandingPageName && (
                <p className={styles.landingPageName} data-testid={formatAsTestId('payment recipient landing page name')}>{getText(partner.LandingPageName)}</p>
              )}
              <p className={styles.recipientName} data-testid={formatAsTestId('payment recipient display name')}>{getText(partner?.DisplayName)}</p>
            </ListGroup.Item>
            <ListGroup.Item as="li" className={styles.billerDisclaimer} role="alert">
              <p>{t('Review_SelectCorrectBillerDisclaimer')}</p>
            </ListGroup.Item>
          </ListGroup>
        </Card>
        { /* Account Details */ }
        <Card className={styles.reviewCard}>
          <ListGroup variant="flush" as="ul">
            <ListGroup.Item as="li">
              <div className={styles.titleButtonContainer}>
                <h2 className={styles.reviewCardTitle}>{t('PaymentInfo_AccountDetailsTitle')}</h2>
                {!isInvoice && (
                  <Button
                    id="editAccountDetails"
                    text={t('ReviewPage_Edit_ButtonText')}
                    fill="outline"
                    ampEvent={events.USER_CLICKED_REVIEWPAGE_EDIT_ACCOUNT_DETAILS}
                    onClick={() => router.push(`${window.location.pathname}`, undefined, { shallow: true })}
                    ariaLabel={t('ReviewPage_EditAccountDetails_ButtonDescription')}
                    className={styles.reviewEditButton}
                  />
                )}
              </div>
              <div>
                <AccountDetailsText
                  sortedAccountDetails={sortedAccountDetails}
                  partner={partner}
                />
              </div>
            </ListGroup.Item>
          </ListGroup>
        </Card>
        { /* Payment Option */ }
        <Card className={styles.reviewCard}>
          <ListGroup variant="flush" as="ul">
            <ListGroup.Item as="li">
              <div className={styles.titleButtonContainer}>
                <h2 className={styles.reviewCardTitle}>{t('PaymentInfo_PaymentOptionTitle')}</h2>
                <Button
                  id="editPaymentOption"
                  text={t('ReviewPage_Edit_ButtonText')}
                  fill="outline"
                  ampEvent={events.USER_CLICKED_REVIEWPAGE_EDIT_PAYMENT_OPTION}
                  onClick={() => router.push(`${window.location.pathname}#payment-option`, undefined, { shallow: true })}
                  ariaLabel={t('ReviewPage_EditPaymentOption_ButtonDescription')}
                  className={styles.reviewEditButton}
                />
              </div>
              <div className={styles.poContentContainer}>
                <div className={styles.poTitleImagesContainer}>
                  <div>
                    <PaymentOptionImages
                      paymentOptionName={paymentOption?.Name}
                      height="28px"
                      gap="2px"
                    />
                  </div>
                  {paymentOption?.Name !== 'PayPal' && (
                    <span data-testid={formatAsTestId('payment option title')}>
                      <ReplaceTextWithElement
                        text={getText(paymentOption?.Title)}
                        replaceWith={<i>Interac</i>}
                        replaceKey="Interac"
                      />
                    </span>
                  )}
                </div>
                <p className={styles.infoHeading}>
                  <b>
                    {`${t('PaymentInfo_ProcessTimeLabel')}: `}
                  </b>
                  <span data-testid={formatAsTestId('payment option delivery')}>
                    {getText(paymentOption?.Delivery)}
                  </span>
                </p>
                <p className={styles.infoHeading}>
                  <b>
                    {`${t('PaymentInfo_ProcessingFeeLabel')}: `}
                  </b>
                  <span data-testid={formatAsTestId('payment option processing fee')}>
                    {calculatedFeeString}
                  </span>
                </p>
              </div>
            </ListGroup.Item>
            {isPOCreditCard && (
              <ListGroup.Item as="li">
                <Form
                  onSubmit={onSubmit}
                  validate={(values) => validate(values)}
                  render={({
                    handleSubmit,
                    invalid,
                    errors,
                  }) => {
                    submit = async (event) => {
                      invalidFormAlert(invalid, errors);
                      await handleSubmit(event);
                    };
                    return (
                      <form onSubmit={submit}>
                        <TextField
                          label={t('ReviewPage_PostalCodeField_Label')}
                          formatText={t('ReviewPage_PostalCodeField_HelpText')}
                          id="postalCode"
                          name="postalCode"
                          autoComplete="postal-code"
                          className={styles.postalCodeFieldContainer}
                        />
                      </form>
                    );
                  }}
                />
              </ListGroup.Item>
            )}
            {additionalPaymentOptionInfo && (
              <ListGroup.Item as="li" className={styles.additionalPaymentOptionInfo}>
                <p>
                  <ReplaceTextWithElement
                    text={t(additionalPaymentOptionInfo)}
                    replaceWith={<i>Interac</i>}
                    replaceKey="Interac"
                  />
                </p>
              </ListGroup.Item>
            )}
          </ListGroup>
        </Card>
        { /* Payment total */ }
        <Card className={styles.reviewCard}>
          <ListGroup variant="flush" as="ul">
            <ListGroup.Item as="li">
              <h2 className={styles.reviewCardTitle}>{t('PaymentInformation_Payment_Total').replace(': xPaymentTotal', '')}</h2>
              <p className={styles.infoHeading}>
                <b>
                  {`${t('PaymentAmountField_Name', null, 'en')}: `}
                </b>
                <span data-testid={formatAsTestId('payment amount')}>{FormatAmount(paymentAmount)}</span>
              </p>
              <p className={styles.infoHeading}>
                <b>
                  {`${t('PaymentInfo_ProcessingFeeLabel')}: `}
                </b>
                <span data-testid={formatAsTestId('payment option processing fee second')}>
                  {calculatedFeeString}
                </span>
              </p>
            </ListGroup.Item>
            <ListGroup.Item as="li">
              <h3 className={styles.totalAmount} data-testid={formatAsTestId('payment total amount')}>
                {t('PaymentInformation_Payment_Total').replace('Payment total', 'Total').replace('xPaymentTotal', FormatAmount(paymentOption?.totalAmount))}
              </h3>
            </ListGroup.Item>
          </ListGroup>
        </Card>
        { /* Google reCAPTCHA Disclaimer */ }
        <GoogleRecaptchaDisclaimer />
        { /* Google reCAPTCHA Success Alert */ }
        <Alert msg={reCaptchaAlertMsg} type="success" className={styles.reCaptchaAlert} fullWidth noFocus />
        { /* Button Panel */ }
        <div className={`${isPOPayPal ? styles.wrappingButtonContainer : styles.buttonContainer}`}>
          <Button
            text={t('ReviewPage_Cancel_Button')}
            ampEvent={events.USER_CLICKED_CANCEL_BUTTON}
            ampEventProps={{ Step: 'Payment Review' }}
            onClick={() => setShowConfirmationModal(true)}
            id="cancelButton"
            ariaLabel={t('ReviewPage_Cancel_ButtonDescription')}
          />
          {isPOPayPal ? (
            <PayPal
              createOrder={() => onSubmit('PAYPAL')}
              onClick={payPalOnClick}
            />
          ) : (
            <Button
              loading={loading}
              text={t('PaymentProcess_ButtonContinue')}
              ampEvent={events.USER_CLICKED_CONTINUE_BUTTON}
              ampEventProps={{ Step: 'Payment Review' }}
              onClick={async (event) => {
                if (submit) {
                  await submit(event);
                } else {
                  await onSubmit();
                }
              }}
              ariaLabel={isPOInPerson ? t('ReviewPage_ContinueInPerson_ButtonDescription') : t('ReviewPage_Continue_ButtonDescription')}
              testId={formatAsTestId('payment process continue', 'button')}
            />
          )}
        </div>
      </div>
      { /* Invisible Google reCAPTCHA for PayPal */ }
      <ReCAPTCHA
        style={{ display: 'none' }}
        ref={reCaptchaRef}
        size="invisible"
        sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
        onErrored={onRecaptchaError}
        onChange={reCaptchaOnChange}
      />
      { /* Confirmation Modal */ }
      <ConfirmationModal
        showModal={showConfirmationModal}
        onCancel={() => setShowConfirmationModal(false)}
        onConfirm={handleCancel}
        ampEventPropsPageName="Payment Review"
        stepName={t('ProgressBarStep4')}
        returnFocusTo="cancelButton"
      />
    </>
  );
}
